import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Percent, Loader2, Lock, Info, Pencil, Euro } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { WMS_CATEGORY_OPTIONS } from "../../pages/wms/wmsUtils";

/**
 * VK-Kalkulation / Aufschlags-Regeln (Feb-2026) + kategorie-basierte Abschläge
 * (Jun-2026, User-Wunsch — Umbau 08.06.2026).
 *
 * Drei Sektionen, alle im selben „Formular + Liste"-Stil erfassbar:
 *  1. VK-Kalkulation · Aufschläge — % auf EK-Netto nach Kategorie + EK-Staffel.
 *  2. Abschlag Kommission        — % auf EK-Netto je Kategorie (ein Wert/Kategorie).
 *  3. Abschläge intern           — % auf Netto-VK je Kategorie & Typ
 *                                  (Gebraucht / Garantie / Intern, ein Wert/Kombi).
 *
 * Die Bearbeitung lebt im Händler-Dashboard → Einstellungen → Warenwirtschaft.
 * In der Warenwirtschaft (`/dealer/wms`) wird die Komponente mit `readOnly`
 * gerendert: alles nur anzeigen, nichts änderbar.
 */
const CATS = [{ v: "__all__", l: "Alle Kategorien" }, ...WMS_CATEGORY_OPTIONS];
const catLabel = (v) => CATS.find((c) => c.v === v)?.l || (v ? v : "Alle Kategorien");
const catL = (v) => WMS_CATEGORY_OPTIONS.find((c) => c.v === v)?.l || v;
const fmtEur = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const INTERNAL_TYPES = [
    { k: "gebraucht", l: "Gebraucht" },
    { k: "garantie", l: "Garantie" },
    { k: "intern", l: "Intern" },
];
const typeLabel = (k) => INTERNAL_TYPES.find((t) => t.k === k)?.l || k;

export default function MarkupRulesPanel({ readOnly = false }) {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ category: "__all__", ek_min: "0", ek_max: "", markup_percent: "", note: "" });
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    // Jun-2026 (User-Wunsch) — Festpreise (vorgegebener VK, z. B. Reifen/Zubehör).
    const [fixForm, setFixForm] = useState({ category: "tire", ek_min: "0", ek_max: "", fixed_sell_gross: "", note: "" });
    const [fixEditId, setFixEditId] = useState(null);
    const [priceGivenCats, setPriceGivenCats] = useState([]);
    // Kommissions-Abschläge: { cat: number }
    const [consDiscounts, setConsDiscounts] = useState({});
    const [consForm, setConsForm] = useState({ category: "part", percent: "" });
    // Interne Abschläge: { cat: { gebraucht, garantie, intern } }
    const [internalDiscounts, setInternalDiscounts] = useState({});
    const [intForm, setIntForm] = useState({ category: "part", type: "intern", percent: "" });
    const [discSaving, setDiscSaving] = useState(false);
    // Jun-2026 (User-Wunsch) — Werkstatt-Stundensatz Montage (€/h brutto). AW-Satz = /12.
    const [montageHourlyRate, setMontageHourlyRate] = useState("");
    // Jul-2026 (User-Wunsch) — max. Rabatt auf vorgegebene Artikelpreise (Default 20 %).
    const [maxSaleDiscount, setMaxSaleDiscount] = useState("20");
    // Aug-2026 (User-Wunsch) — vorgemerkter Rabatt auf offizielle AW-Vorgaben (%).
    const [awDiscount, setAwDiscount] = useState("");

    const applySettings = (s) => {
        const cd = {};
        Object.entries(s?.consignment_discounts || {}).forEach(([k, v]) => { cd[k] = Number(v) || 0; });
        setConsDiscounts(cd);
        const idd = {};
        Object.entries(s?.internal_discounts || {}).forEach(([k, v]) => {
            idd[k] = {
                gebraucht: Number(v?.gebraucht) || 0,
                garantie: Number(v?.garantie) || 0,
                intern: Number(v?.intern) || 0,
            };
        });
        setInternalDiscounts(idd);
        setMontageHourlyRate(s?.montage_hourly_rate ? String(s.montage_hourly_rate) : "");
        setPriceGivenCats(Array.isArray(s?.price_given_categories) ? s.price_given_categories : []);
        setMaxSaleDiscount(s?.max_sale_discount_pct != null ? String(s.max_sale_discount_pct) : "20");
        setAwDiscount(s?.official_aw_discount_pct ? String(s.official_aw_discount_pct) : "");
    };
    const load = async () => {
        setLoading(true);
        try {
            const [r, s] = await Promise.all([
                api.get("/wms/markup-rules"),
                api.get("/wms/settings").catch(() => ({ data: {} })),
            ]);
            setRules(Array.isArray(r.data) ? r.data : []);
            applySettings(s.data || {});
        } catch { toast.error("VK-Kalkulation konnte nicht geladen werden"); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        let on = true;
        (async () => {
            try {
                const [r, s] = await Promise.all([
                    api.get("/wms/markup-rules"),
                    api.get("/wms/settings").catch(() => ({ data: {} })),
                ]);
                if (!on) return;
                setRules(Array.isArray(r.data) ? r.data : []);
                applySettings(s.data || {});
            } catch { toast.error("VK-Kalkulation konnte nicht geladen werden"); }
            finally { if (on) setLoading(false); }
        })();
        return () => { on = false; };
    }, []);

    // Speichert eine Teil-Änderung der WMS-Einstellungen (PUT aktualisiert nur die
    // mitgesendeten Felder) und übernimmt das Ergebnis in den lokalen State.
    const saveSettings = async (patch) => {
        setDiscSaving(true);
        try {
            const { data } = await api.put("/wms/settings", patch);
            applySettings(data);
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            return false;
        } finally { setDiscSaving(false); }
    };

    // Jun-2026 (User-Wunsch) — Werkstatt-Stundensatz speichern (AW-Satz = /12).
    const saveMontageRate = async () => {
        const rate = parseFloat(montageHourlyRate);
        if (montageHourlyRate !== "" && (isNaN(rate) || rate < 0)) {
            toast.error("Bitte einen gültigen Stundensatz (≥ 0) angeben");
            return;
        }
        if (await saveSettings({ montage_hourly_rate: montageHourlyRate === "" ? 0 : rate })) {
            toast.success("Werkstatt-Stundensatz gespeichert — AW-Satz aktualisiert");
        }
    };
    // Jul-2026 (User-Wunsch) — max. Rabatt auf Artikelpreise speichern.
    const saveMaxSaleDiscount = async () => {
        const pct = parseFloat(maxSaleDiscount);
        if (isNaN(pct) || pct < 0 || pct > 100) {
            toast.error("Bitte einen Wert zwischen 0 und 100 % angeben");
            return;
        }
        if (await saveSettings({ max_sale_discount_pct: pct })) {
            toast.success("Maximaler Artikel-Rabatt gespeichert");
        }
    };
    const addConsDiscount = async () => {
        const pct = parseFloat(consForm.percent);
        if (isNaN(pct) || pct <= 0 || pct > 100) { toast.error("Bitte einen Abschlag zwischen 0 und 100 % angeben"); return; }
        const next = { ...consDiscounts, [consForm.category]: pct };
        if (await saveSettings({ consignment_discounts: next })) {
            toast.success("Kommissions-Abschlag gespeichert");
            setConsForm((f) => ({ ...f, percent: "" }));
        }
    };
    const delConsDiscount = async (cat) => {
        const next = { ...consDiscounts };
        delete next[cat];
        if (await saveSettings({ consignment_discounts: next })) toast.success("Abschlag entfernt");
    };

    // --- Interne Abschläge -----------------------------------------------------
    // Aug-2026 (User-Wunsch): Rabatt auf offizielle AW-Vorgaben vormerken.
    const saveAwDiscount = async () => {
        const pct = awDiscount === "" ? 0 : parseFloat(awDiscount);
        if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Bitte einen Wert zwischen 0 und 100 % angeben"); return; }
        if (await saveSettings({ official_aw_discount_pct: pct })) {
            toast.success(pct > 0 ? `AW-Rabatt von ${pct} % vorgemerkt` : "AW-Rabatt entfernt");
        }
    };
    const addIntDiscount = async () => {
        const pct = parseFloat(intForm.percent);
        if (isNaN(pct) || pct <= 0 || pct > 100) { toast.error("Bitte einen Abschlag zwischen 0 und 100 % angeben"); return; }
        const next = { ...internalDiscounts };
        const row = { gebraucht: 0, garantie: 0, intern: 0, ...(next[intForm.category] || {}) };
        row[intForm.type] = pct;
        next[intForm.category] = row;
        if (await saveSettings({ internal_discounts: next })) {
            toast.success("Interner Abschlag gespeichert");
            setIntForm((f) => ({ ...f, percent: "" }));
        }
    };
    const delIntDiscount = async (cat, type) => {
        const next = { ...internalDiscounts };
        const row = { ...(next[cat] || {}) };
        row[type] = 0;
        if (!row.gebraucht && !row.garantie && !row.intern) delete next[cat];
        else next[cat] = row;
        if (await saveSettings({ internal_discounts: next })) toast.success("Abschlag entfernt");
    };

    const addRule = async () => {
        const mp = parseFloat(form.markup_percent);
        if (isNaN(mp) || mp < 0) { toast.error("Bitte einen gültigen Aufschlag in % angeben"); return; }
        setSaving(true);
        try {
            const payload = {
                category: form.category === "__all__" ? null : form.category,
                ek_min: parseFloat(form.ek_min) || 0,
                ek_max: form.ek_max === "" ? null : parseFloat(form.ek_max),
                markup_percent: mp,
                rule_type: "markup",
                note: form.note || null,
            };
            if (editId) await api.patch(`/wms/markup-rules/${editId}`, payload);
            else await api.post("/wms/markup-rules", payload);
            toast.success(editId ? "Aufschlags-Regel aktualisiert" : "Aufschlags-Regel gespeichert");
            setForm({ category: "__all__", ek_min: "0", ek_max: "", markup_percent: "", note: "" });
            setEditId(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Regel konnte nicht gespeichert werden"); }
        finally { setSaving(false); }
    };
    const editRule = (r) => {
        setEditId(r.id);
        setForm({
            category: r.category || "__all__",
            ek_min: String(r.ek_min ?? "0"),
            ek_max: r.ek_max != null ? String(r.ek_max) : "",
            markup_percent: String(r.markup_percent ?? ""),
            note: r.note || "",
        });
    };
    const cancelEdit = () => { setEditId(null); setForm({ category: "__all__", ek_min: "0", ek_max: "", markup_percent: "", note: "" }); };

    // Festpreis-Regeln (rule_type=fixed) -------------------------------------
    const addFixed = async () => {
        const vk = parseFloat(fixForm.fixed_sell_gross);
        if (isNaN(vk) || vk <= 0) { toast.error("Bitte einen Endkunden-VK (brutto) > 0 angeben"); return; }
        setSaving(true);
        try {
            const payload = {
                category: fixForm.category === "__all__" ? null : fixForm.category,
                ek_min: parseFloat(fixForm.ek_min) || 0,
                ek_max: fixForm.ek_max === "" ? null : parseFloat(fixForm.ek_max),
                rule_type: "fixed",
                fixed_sell_gross: vk,
                markup_percent: 0,
                note: fixForm.note || null,
            };
            if (fixEditId) await api.patch(`/wms/markup-rules/${fixEditId}`, payload);
            else await api.post("/wms/markup-rules", payload);
            toast.success(fixEditId ? "Festpreis-Regel aktualisiert" : "Festpreis-Regel gespeichert");
            setFixForm({ category: "tire", ek_min: "0", ek_max: "", fixed_sell_gross: "", note: "" });
            setFixEditId(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Regel konnte nicht gespeichert werden"); }
        finally { setSaving(false); }
    };
    const editFixed = (r) => {
        setFixEditId(r.id);
        setFixForm({
            category: r.category || "__all__",
            ek_min: String(r.ek_min ?? "0"),
            ek_max: r.ek_max != null ? String(r.ek_max) : "",
            fixed_sell_gross: String(r.fixed_sell_gross ?? ""),
            note: r.note || "",
        });
    };
    const cancelFixEdit = () => { setFixEditId(null); setFixForm({ category: "tire", ek_min: "0", ek_max: "", fixed_sell_gross: "", note: "" }); };

    // „Preise vorgegeben"-Kategorien (kein Auto-VK) ---------------------------
    const togglePriceGiven = async (cat) => {
        const next = priceGivenCats.includes(cat)
            ? priceGivenCats.filter((c) => c !== cat)
            : [...priceGivenCats, cat];
        setPriceGivenCats(next);
        if (!(await saveSettings({ price_given_categories: next }))) load();
    };

    const del = async (id) => {
        if (!window.confirm("Aufschlags-Regel löschen?")) return;
        try { await api.delete(`/wms/markup-rules/${id}`); load(); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    // Flache Liste der internen Abschläge: [{ cat, type, pct }]
    const intRows = [];
    Object.entries(internalDiscounts).forEach(([cat, row]) => {
        INTERNAL_TYPES.forEach((t) => {
            const pct = Number(row?.[t.k]) || 0;
            if (pct > 0) intRows.push({ cat, type: t.k, pct });
        });
    });
    const consRows = Object.entries(consDiscounts)
        .filter(([, v]) => Number(v) > 0)
        .map(([cat, pct]) => ({ cat, pct: Number(pct) }));
    const markupRules = rules.filter((r) => r.rule_type !== "fixed");
    const fixedRules = rules.filter((r) => r.rule_type === "fixed");

    return (
        <div className="space-y-6" data-testid="markup-rules-panel">
            {/* Aug-2026 (User-Wunsch): fest hinterlegte Fallback-Regel sichtbar machen */}
            <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-[12px] text-sky-900" data-testid="uvp-fallback-rule-info">
                <b>Fest hinterlegte Fallback-Regel:</b> Greift bei „⚡ VK neu kalkulieren" keine EK-Aufschlagsregel,
                wird der VK automatisch als <b>Hersteller-UVP × 1,3</b> berechnet — nur für <b>Ersatzteile, Reifen und Flüssigkeiten</b>.
                {" "}<b>Zubehör ist ausgenommen</b> (UVP wird dort werblich genutzt), ebenso Festpreise, Miete, Zulassung &amp; Dienstleistungen.
                Der Hersteller-UVP bleibt dabei immer im Feld „UVP brutto" erhalten.
            </div>
            {readOnly && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2" data-testid="markup-readonly-hint">
                    <Lock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-900">
                        Anzeige (schreibgeschützt). Die VK-Kalkulation wird im{" "}
                        <Link to="/dashboard?tab=settings#settings-warenwirtschaft" className="font-bold underline" data-testid="markup-edit-link">
                            Händler-Dashboard → Einstellungen → Warenwirtschaft
                        </Link>{" "}bearbeitet.
                    </p>
                </div>
            )}

            {/* === Aufschlags-Regeln (% auf EK-Netto) === */}
            <div className="bg-white border border-gray-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-[#111827]" />
                    <h3 className="font-display font-black text-lg">VK-Kalkulation · Aufschläge</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                    Aufschläge nach Kategorie und EK-Netto-Staffel. Beim Anlegen neuer Artikel ohne eigenen VK
                    wird der Verkaufspreis automatisch aus dem EK berechnet (kategorie-spezifische Regeln haben Vorrang).
                </p>

                {!readOnly && (
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="markup-rule-form">
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kategorie</label>
                            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="markup-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">EK ab (€)</label>
                            <Input type="number" min="0" step="0.01" value={form.ek_min} onChange={(e) => setForm((f) => ({ ...f, ek_min: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="markup-form-ekmin" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">EK bis (€)</label>
                            <Input type="number" min="0" step="0.01" placeholder="∞" value={form.ek_max} onChange={(e) => setForm((f) => ({ ...f, ek_max: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="markup-form-ekmax" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Aufschlag %</label>
                            <Input type="number" min="0" step="1" placeholder="z. B. 40" value={form.markup_percent} onChange={(e) => setForm((f) => ({ ...f, markup_percent: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="markup-form-percent" />
                        </div>
                        <Button onClick={addRule} disabled={saving} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm h-9" data-testid="markup-form-add">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> {editId ? "Aktualisieren" : "Regel"}</>}
                        </Button>
                        {editId && (
                            <button onClick={cancelEdit} className="text-xs font-bold text-gray-500 hover:text-gray-800 md:col-span-6 text-left" data-testid="markup-form-cancel">Bearbeitung abbrechen</button>
                        )}
                    </div>
                )}

                <div className="mt-4">
                    {loading ? (
                        <div className="text-center py-6 text-gray-400 text-sm">Lade…</div>
                    ) : markupRules.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm" data-testid="markup-rules-empty">Noch keine Aufschlags-Regeln.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="markup-rules-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="py-2">Kategorie</th>
                                    <th className="py-2">EK-Spanne (netto)</th>
                                    <th className="py-2 text-right">Aufschlag</th>
                                    <th className="py-2">Notiz</th>
                                    {!readOnly && <th className="py-2 w-20"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {markupRules.map((r) => (
                                    <tr key={r.id} className={`border-b border-gray-100 ${!readOnly ? "cursor-pointer hover:bg-gray-50" : ""} ${editId === r.id ? "bg-amber-50" : ""}`} data-testid={`markup-rule-${r.id}`} onClick={readOnly ? undefined : () => editRule(r)}>
                                        <td className="py-2 font-bold">{catLabel(r.category)}</td>
                                        <td className="py-2 font-mono-num">{fmtEur(r.ek_min)} – {r.ek_max != null ? fmtEur(r.ek_max) : "∞"}</td>
                                        <td className="py-2 text-right font-mono-num font-bold text-emerald-700">+{r.markup_percent} %</td>
                                        <td className="py-2 text-gray-500">{r.note || "—"}</td>
                                        {!readOnly && (
                                            <td className="py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => editRule(r)} className="p-1.5 rounded-sm text-gray-400 hover:text-[#111827] hover:bg-gray-100" data-testid={`markup-rule-edit-${r.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => del(r.id)} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`markup-rule-del-${r.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* === Jun-2026 (User-Wunsch) — Festpreise (vorgegebener VK, z. B. Reifen/Zubehör) === */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="fixed-price-card">
                <div className="flex items-center gap-2 mb-1">
                    <Euro className="w-4 h-4 text-emerald-700" />
                    <h3 className="font-display font-black text-lg text-emerald-900">VK-Kalkulation · Festpreise</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Für Kategorien mit <strong>vorgegebenem Endkunden-VK</strong> (z. B. Reifen &amp; Zubehör): statt eines Aufschlags wird ein <strong>fester Brutto-VK</strong> hinterlegt. Festpreis-Regeln haben Vorrang vor Aufschlägen.</span>
                </p>

                {!readOnly && (
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="fixed-price-form">
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kategorie</label>
                            <Select value={fixForm.category} onValueChange={(v) => setFixForm((f) => ({ ...f, category: v }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="fixed-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">EK ab (€)</label>
                            <Input type="number" min="0" step="0.01" value={fixForm.ek_min} onChange={(e) => setFixForm((f) => ({ ...f, ek_min: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="fixed-form-ekmin" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">EK bis (€)</label>
                            <Input type="number" min="0" step="0.01" placeholder="∞" value={fixForm.ek_max} onChange={(e) => setFixForm((f) => ({ ...f, ek_max: e.target.value }))} className="rounded-sm mt-1 h-9" data-testid="fixed-form-ekmax" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">VK brutto (€)</label>
                            <Input type="number" min="0" step="0.01" placeholder="z. B. 149,00" value={fixForm.fixed_sell_gross} onChange={(e) => setFixForm((f) => ({ ...f, fixed_sell_gross: e.target.value }))} className="rounded-sm mt-1 h-9 font-mono-num" data-testid="fixed-form-vk" />
                        </div>
                        <Button onClick={addFixed} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm h-9" data-testid="fixed-form-add">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> {fixEditId ? "Aktualisieren" : "Festpreis"}</>}
                        </Button>
                        {fixEditId && (
                            <button onClick={cancelFixEdit} className="text-xs font-bold text-gray-500 hover:text-gray-800 md:col-span-6 text-left" data-testid="fixed-form-cancel">Bearbeitung abbrechen</button>
                        )}
                    </div>
                )}

                <div className="mt-4">
                    {fixedRules.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm" data-testid="fixed-price-empty">Noch keine Festpreis-Regeln.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="fixed-price-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="py-2">Kategorie</th>
                                    <th className="py-2">EK-Spanne (netto)</th>
                                    <th className="py-2 text-right">VK brutto</th>
                                    <th className="py-2">Notiz</th>
                                    {!readOnly && <th className="py-2 w-20"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {fixedRules.map((r) => (
                                    <tr key={r.id} className={`border-b border-gray-100 ${!readOnly ? "cursor-pointer hover:bg-gray-50" : ""} ${fixEditId === r.id ? "bg-emerald-50" : ""}`} data-testid={`fixed-rule-${r.id}`} onClick={readOnly ? undefined : () => editFixed(r)}>
                                        <td className="py-2 font-bold">{catLabel(r.category)}</td>
                                        <td className="py-2 font-mono-num">{fmtEur(r.ek_min)} – {r.ek_max != null ? fmtEur(r.ek_max) : "∞"}</td>
                                        <td className="py-2 text-right font-mono-num font-bold text-emerald-700">{fmtEur(r.fixed_sell_gross)}</td>
                                        <td className="py-2 text-gray-500">{r.note || "—"}</td>
                                        {!readOnly && (
                                            <td className="py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => editFixed(r)} className="p-1.5 rounded-sm text-gray-400 hover:text-[#111827] hover:bg-gray-100" data-testid={`fixed-rule-edit-${r.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => del(r.id)} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`fixed-rule-del-${r.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Preise vorgegeben: keine Auto-Kalkulation für diese Kategorien */}
                <div className="mt-5 pt-4 border-t border-gray-100" data-testid="price-given-block">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Preise vorgegeben (keine automatische VK-Kalkulation)</div>
                    <p className="text-[11px] text-gray-500 mb-2">Bei diesen Kategorien werden EK &amp; VK immer aus der Artikel-/Importdatei übernommen und nie automatisch berechnet.</p>
                    <div className="flex flex-wrap gap-2">
                        {WMS_CATEGORY_OPTIONS.map((c) => {
                            const on = priceGivenCats.includes(c.v);
                            return (
                                <button key={c.v} type="button" disabled={readOnly || discSaving}
                                    onClick={() => togglePriceGiven(c.v)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${on ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"}`}
                                    data-testid={`price-given-${c.v}`}>
                                    {c.l}{on ? " ✓" : ""}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* === Jun-2026 (User-Wunsch) — Werkstatt-Stundensatz Montage (AW-Satz) === */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="montage-rate-card">
                <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-amber-600" />
                    <h3 className="font-display font-black text-lg text-amber-900">Werkstatt-Stundensatz · Montage (AW-Satz)</h3>
                </div>
                <p className="text-sm text-gray-600 mb-1 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Ein Wert an einer Stelle: <strong>1 AW = 6 Minuten</strong> ⇒ 10 AW = 1 Stunde ⇒ der <strong>AW-Satz</strong> für die automatische Zubehör-Montageberechnung wird <strong>= Stundensatz ÷ 10</strong> abgeleitet. Dieser Stundensatz hat Vorrang vor einem manuell markierten Lohn/AW-Artikel.</span>
                </p>
                <p className="text-xs text-gray-500 mb-4 pl-5">
                    Der Stundensatz wird <strong>brutto</strong> erfasst — in Aufträge wird immer der abgeleitete <strong>NETTO-AW-Satz</strong> übernommen (die MwSt kommt erst in der Rechnung dazu).
                    Diese Einstellung erscheint auch unter <strong>Händler-Dashboard → Einstellungen → Warenwirtschaft</strong> — es ist ein und derselbe Wert (eine Datenquelle, org-weit).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end bg-amber-50 border border-amber-200 rounded-sm p-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Stundensatz (€/h, brutto)</label>
                        <Input type="number" min="0" step="0.01" placeholder="z. B. 165,40" value={montageHourlyRate}
                            onChange={(e) => setMontageHourlyRate(e.target.value)} disabled={readOnly}
                            className="rounded-sm mt-1 h-9 font-mono-num" data-testid="montage-rate-hourly-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Ergibt AW-Satz (€/AW)</label>
                        <div className="rounded-sm mt-1 h-9 px-3 flex items-center bg-white border border-amber-200 font-mono-num text-sm font-bold text-amber-900" data-testid="montage-rate-per-aw">
                            {fmtEur((parseFloat(montageHourlyRate) || 0) / 10 / 1.19)}&nbsp;<span className="text-[10px] font-bold text-amber-700">netto → in den Auftrag</span>
                        </div>
                        <div className="text-[10px] text-amber-700 mt-0.5 font-mono-num" data-testid="montage-rate-per-aw-gross">
                            = {fmtEur((parseFloat(montageHourlyRate) || 0) / 10)} brutto (inkl. 19 % MwSt)
                        </div>
                    </div>
                    {!readOnly && (
                        <Button onClick={saveMontageRate} disabled={discSaving}
                            className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm h-9" data-testid="montage-rate-save">
                            {discSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                        </Button>
                    )}
                </div>
            </div>

            {/* === Jul-2026 (User-Wunsch) — Preisschutz: max. Rabatt auf Artikelpreise === */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="max-discount-card">
                <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-red-600" />
                    <h3 className="font-display font-black text-lg text-red-900">Preisschutz · Max. Rabatt auf Artikelpreise</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Vorgegebene Artikelpreise dürfen bei <strong>Rechnung- und Auftragserstellung</strong> nur per <strong>Rabatt</strong> gesenkt werden — nie durch direkte Preiseingabe nach unten. Nach oben bleibt der Preis frei anpassbar. Hier legst du den <strong>maximal erlaubten Rabatt</strong> fest (Voreinstellung 20&nbsp;%).</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end bg-red-50 border border-red-200 rounded-sm p-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-red-800">Max. Rabatt (%)</label>
                        <Input type="number" min="0" max="100" step="0.5" placeholder="20" value={maxSaleDiscount}
                            onChange={(e) => setMaxSaleDiscount(e.target.value)} disabled={readOnly}
                            className="rounded-sm mt-1 h-9 font-mono-num" data-testid="max-discount-input" />
                    </div>
                    <div className="md:col-span-1 text-xs text-red-800 font-semibold">
                        Gilt für alle WWS-Artikel in „Neue Rechnung“, Direktverkauf und Werkstattaufträgen.
                    </div>
                    {!readOnly && (
                        <Button onClick={saveMaxSaleDiscount} disabled={discSaving}
                            className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm h-9" data-testid="max-discount-save">
                            {discSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                        </Button>
                    )}
                </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="cons-discount-card">
                <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-purple-700" />
                    <h3 className="font-display font-black text-lg text-purple-900">Abschlag Kommission</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Prozentualer Abschlag auf den <strong>EK-Netto</strong> je Kategorie für Kommissionsware — senkt den effektiven EK und verbessert die Marge. Ein Wert je Kategorie.</span>
                </p>

                {!readOnly && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="cons-discount-form">
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kategorie</label>
                            <Select value={consForm.category} onValueChange={(v) => setConsForm((f) => ({ ...f, category: v }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="cons-discount-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {WMS_CATEGORY_OPTIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kommission % (EK)</label>
                            <Input type="number" min="0" max="100" step="0.1" placeholder="z. B. 15" value={consForm.percent}
                                   onChange={(e) => setConsForm((f) => ({ ...f, percent: e.target.value }))}
                                   className="rounded-sm mt-1 h-9 font-mono-num" data-testid="cons-discount-form-percent" />
                        </div>
                        <Button onClick={addConsDiscount} disabled={discSaving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-sm h-9" data-testid="cons-discount-form-add">
                            {discSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Hinzufügen</>}
                        </Button>
                    </div>
                )}

                <div className="mt-4">
                    {consRows.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm" data-testid="cons-discount-empty">Noch keine Kommissions-Abschläge.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="cons-discount-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="py-2">Kategorie</th>
                                    <th className="py-2 text-right">Kommission % (EK)</th>
                                    {!readOnly && <th className="py-2 w-10"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {consRows.map((row) => (
                                    <tr key={row.cat} className="border-b border-gray-100" data-testid={`cons-discount-row-${row.cat}`}>
                                        <td className="py-2 font-bold">{catL(row.cat)}</td>
                                        <td className="py-2 text-right font-mono-num font-bold text-purple-700">−{row.pct} %</td>
                                        {!readOnly && (
                                            <td className="py-2 text-right">
                                                <button onClick={() => delConsDiscount(row.cat)} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`cons-discount-del-${row.cat}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* === Abschläge intern (% auf Netto-VK) === */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="internal-discount-card">
                <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-sky-700" />
                    <h3 className="font-display font-black text-lg text-sky-900">Abschläge intern</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Prozentualer Abschlag auf den <strong>Netto-VK</strong> je Kategorie und Typ (Gebraucht / Garantie / Intern) — z. B. für interne Aufträge. Ein Wert je Kategorie &amp; Typ.</span>
                </p>

                {/* Aug-2026 (User-Wunsch): vorgemerkter Rabatt auf offizielle AW-Vorgaben */}
                <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 mb-4" data-testid="aw-discount-block">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-sky-800">
                        Rabatt auf offizielle AW-Vorgaben (%)
                    </label>
                    <p className="text-[11px] text-gray-600 mt-0.5 mb-2">
                        Wird bei Lohn-Positionen mit dem offiziellen AW-Satz automatisch als Rabatt vorgemerkt
                        (im Auftrag sichtbar und jederzeit änderbar).
                    </p>
                    <div className="flex gap-2 items-center">
                        <Input type="number" min="0" max="100" step="0.5" placeholder="z. B. 40" value={awDiscount}
                               onChange={(e) => setAwDiscount(e.target.value)} disabled={readOnly}
                               className="rounded-sm h-9 w-28 font-mono-num" data-testid="aw-discount-input" />
                        {!readOnly && (
                            <Button onClick={saveAwDiscount} disabled={discSaving}
                                    className="bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-sm h-9" data-testid="aw-discount-save">
                                {discSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                            </Button>
                        )}
                        {Number(awDiscount) > 0 && (
                            <span className="text-xs font-bold text-sky-700 font-mono-num" data-testid="aw-discount-active">−{awDiscount} % auf AW vorgemerkt</span>
                        )}
                    </div>
                </div>

                {!readOnly && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="internal-discount-form">
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kategorie</label>
                            <Select value={intForm.category} onValueChange={(v) => setIntForm((f) => ({ ...f, category: v }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="internal-discount-form-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {WMS_CATEGORY_OPTIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Typ</label>
                            <Select value={intForm.type} onValueChange={(v) => setIntForm((f) => ({ ...f, type: v }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="internal-discount-form-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {INTERNAL_TYPES.map((t) => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Abschlag % (VK)</label>
                            <Input type="number" min="0" max="100" step="0.1" placeholder="z. B. 20" value={intForm.percent}
                                   onChange={(e) => setIntForm((f) => ({ ...f, percent: e.target.value }))}
                                   className="rounded-sm mt-1 h-9 font-mono-num" data-testid="internal-discount-form-percent" />
                        </div>
                        <Button onClick={addIntDiscount} disabled={discSaving} className="bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-sm h-9" data-testid="internal-discount-form-add">
                            {discSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Hinzufügen</>}
                        </Button>
                    </div>
                )}

                <div className="mt-4">
                    {intRows.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm" data-testid="internal-discount-empty">Noch keine internen Abschläge.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="internal-discount-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="py-2">Kategorie</th>
                                    <th className="py-2">Typ</th>
                                    <th className="py-2 text-right">Abschlag % (VK)</th>
                                    {!readOnly && <th className="py-2 w-10"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {intRows.map((row) => (
                                    <tr key={`${row.cat}-${row.type}`} className="border-b border-gray-100" data-testid={`internal-discount-row-${row.cat}-${row.type}`}>
                                        <td className="py-2 font-bold">{catL(row.cat)}</td>
                                        <td className="py-2">{typeLabel(row.type)}</td>
                                        <td className="py-2 text-right font-mono-num font-bold text-sky-700">−{row.pct} %</td>
                                        {!readOnly && (
                                            <td className="py-2 text-right">
                                                <button onClick={() => delIntDiscount(row.cat, row.type)} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`internal-discount-del-${row.cat}-${row.type}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
